import Axios from "axios";
import settings from "../settings";

export default class SearchService {
  getTagByDisplayName(displayName) {
    return settings.citiesTags.find((city) => city.displayName === displayName);
  }

  isDefaultSearch({
    cityTag,
    checkIn,
    checkOut,
    sortKey,
    guests,
    bedrooms,
    searchText,
    tags,
  }) {
    return (
      cityTag === "DXB" &&
      !checkIn &&
      !checkOut &&
      sortKey === "_id" &&
      parseInt(guests) === 1 &&
      !bedrooms.length &&
      !searchText &&
      !tags.length
    );
  }

  isDefaultPage({checkIn,
    checkOut,
    sortKey,
    guests,
    bedrooms,
    searchText,
    page,
    tags,}){
      return (
        !checkIn &&
        !checkOut &&
        sortKey === "_id" &&
        parseInt(guests) === 1 &&
        !bedrooms.length &&
        !searchText &&
        !tags.length && 
        parseInt(page) === 1
      );
  }

  prepareListingParameters(params) {
    let {
      cityTag,
      checkIn,
      checkOut,
      guests,
      sort,
      page,
      tags,
      bedrooms,
      searchText,
      listingIds,
      clearQuery,
      is_star
    } = params;

    const filters = [{ field: "type", operator: "$ne", value: "MTL" }];

    let cityTags = [cityTag];
    let sorting = sort.key;
    let available = {};

    if (!searchText || searchText.length === 0) {
      if (!checkIn || !checkOut) {
        filters.push({
          field: "accommodates",
          operator: "$gt",
          value: guests - 1,
        });
      }

      if (tags?.length) {
        filters.push({
          value: tags,
          operator: "$in",
          field: "tags",
        });
      }

      if (bedrooms?.length) {
        filters.push({
          value: bedrooms,
          operator: "$in",
          field: "bedrooms",
        });
      }

      available = {
        checkIn: checkIn,
        checkOut: checkOut,
        minOccupancy: parseInt(guests),
      };

      if (checkIn && checkOut && sort.reservationKey) {
        sorting = sort.reservationKey;
      }
    }

    return {
      filters,
      available,
      sorting,
      page,
      cityTags,
      listingIds,
      clearQuery,
      is_star,
    };
  }

  getAvailableListingsCount(params) {
    let { page, searchText, listingIds, clearQuery } = params;

    const { filters, available, sorting, cityTags } =
      this.prepareListingParameters(params);

    filters.push({
      value: ["STAR"],
      operator: "$nin",
      field: "tags",
    });

    return new Promise((resolve, reject) => {
      return this.getListingsCount(
        filters,
        available,
        sorting,
        page,
        searchText,
        cityTags,
        listingIds,
        clearQuery
      )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getMapProperty(params) {
    return new Promise((resolve, reject) => {
			return Axios.get(`${settings.guestyApiUrlV2}/map/property`, {
				params,
			})
				.then((res) => resolve(res.data))
				.catch((error) => reject(error));
		});
  }

  getAvailableListings(params) {
    let { page, searchText, listingIds, clearQuery, omitPagination } = params;

    const { filters, available, sorting, cityTags, is_star } =
      this.prepareListingParameters(params);

    const defaultFilters = this.isDefaultSearch({
      ...params,
      sortKey: sorting,
    });


    if (defaultFilters && !listingIds?.length) {
      filters.push({
        value: ["STAR"],
        operator: parseInt(page) === 1 ? "$in" : "$nin",
        field: "tags",
      });
      if (parseInt(page) === 1) {
        cityTags.splice(0, 1);
      } else {
        page--;
      }
    }

    return new Promise((resolve, reject) => {
      return this.getListings(
        filters,
        available,
        sorting,
        page,
        searchText,
        cityTags,
        listingIds,
        clearQuery,
        omitPagination,
        is_star
      )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getAvailableListingsForMap(params) {
    const { filters, sorting, is_star } =
      this.prepareListingParameters(params);
    const { guests, bedrooms, cityTag, tags, checkIn, checkOut, page } = params;

    return new Promise((resolve, reject) => {
      return this.getMapListings(
        guests,
        bedrooms,
        cityTag,
        tags,
        checkIn,
        checkOut,
        filters,
        sorting,
        is_star,
        page
      )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getCertainListings(listingsIds) {
    return new Promise((resolve, reject) => {
      return this.getListingsById(listingsIds)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getAvailableCities() {
    return new Promise((resolve, reject) => {
      return this.getCities()
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getCities() {
    return Axios.get(`${settings.guestyApiUrlV3}/listings/cities`);
  }

  getListingsCount(filters, available, sort, page, searchText, tags) {
    const params = new URLSearchParams({
      is_star : 0,
      page: 1,
      active: true,
      listed: true,
      fields: "_id",
      limit: 1,
      skip: 0,
      filters: JSON.stringify(filters),
      tags,
    });

    if (available.checkIn && available.checkOut) {
      params.append("available", JSON.stringify(available));
    }
    if (sort) {
      params.append("sort", sort);
    }
    if (searchText) {
      params.append("q", searchText);
    }

    return Axios.get(`${settings.guestyApiUrlV3}/listings`, {
      params,
    });
  }

  getListings(
    filters,
    available,
    sort,
    page,
    searchText,
    tags,
    listingsIds,
    clearQuery = false,
    omitPagination = false,
    is_star,
  ) {
    const params = new URLSearchParams({
      page,
      is_star,
      active: true,
      listed: true,
      fields:
        "_id terms taxes accommodates calendarRules title beds bedrooms bathrooms propertyType address.full address.city address.country address.state address.street address.lng address.lat publicDescription picture.regular picture.large defaultCheckInTime defaultCheckOutTime prices amenities pictures markups isListed",
      ...(!clearQuery &&
        !omitPagination && {
          limit: settings.itemsPerPage(),
        }),
      ...(!clearQuery &&
        !omitPagination && {
          skip: settings.itemsPerPage() * (page - 1),
        }),
      ...(!clearQuery && { filters: JSON.stringify(filters) }),
      ...(!clearQuery && { tags }),
    });

    if (!clearQuery) {
      if (available.checkIn && available.checkOut) {
        params.append("available", JSON.stringify(available));
      }
      if (sort) {
        params.append("sort", sort);
      }
      if (searchText) {
        params.append("q", searchText);
      }
    }
    if (listingsIds) {
      params.append("ids", listingsIds.join(","));
    }

    return Axios.get(`${settings.guestyApiUrlV3}/listings`, {
      params,
    });
  }

  getMapListings(accommodates, bedrooms, tags, area, checkIn, checkOut, filters, sort, is_star, page) {
    const params = new URLSearchParams({
      page,
      is_star,
      active: true,
      listed: true,
      fields:
        "_id terms taxes accommodates calendarRules title beds bedrooms bathrooms propertyType address.full address.city address.country address.state address.street address.lng address.lat publicDescription picture.regular picture.large defaultCheckInTime defaultCheckOutTime prices amenities pictures markups isListed",
      limit: settings.itemsPerPage(),
      skip: settings.itemsPerPage() * (page - 1),
      ...(filters && { filters: JSON.stringify(filters) }),
      tags,
      ...(parseInt(page) === 1 && { full: true }),
    });

    if (checkIn && checkOut) {
      params.append("available", JSON.stringify({
        checkIn,
        checkOut,
        minOccupancy: parseInt(accommodates),
      }));
    }

    if (sort) {
      params.append("sort", sort);
    }

    return Axios.get(`${settings.guestyApiUrlMapV4}/map/`, { params });
  }

  getListingsById(listingsIds) {
    return Axios.get(`${settings.guestyApiUrlV2}/listings`, {
      params: {
        active: true,
        listed: true,
        ids: listingsIds.join(","),
        fields:
          "_id terms taxes accommodates calendarRules title beds bedrooms bathrooms propertyType address.full address.city address.country address.state address.street address.lng address.lat publicDescription picture.regular picture.large defaultCheckInTime defaultCheckOutTime prices amenities pictures markups isListed",
      },
    });
  }

  // 216 : city-guide

  getCityGuide(tag) {
    return new Promise((resolve, reject) => {
      return Axios.get(
        `https://web-api.frankporter.com/wp-json/wp/v2/posts?per_page=12&page=1&order=desc&orderby=date&tags=${tag}`
      )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}

export const searchService = new SearchService();
